import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { CameraPathJob, ShotKeyframe, useShotCamera } from './StudioCamera';
import { useVcamLink } from '../../vcam/use-vcam-link';
import { poseTarget, VcamControl, VcamPose, VcamState } from '../../vcam/vcam';

/**
 * Camera takes for the 3D studios — keyframed moves, phone-filmed moves, and
 * the viewport recording that turns either into a video.
 *
 * Lived inside the furniture studio until tiles needed the same thing. Both
 * studios share a camera model (useShotCamera, CameraPathAnimator), so what
 * differs between them is only how each dresses its scene for a recording and
 * what the phone is told about the subject — those come in as options.
 */

// SCENES = cuts. Each scene has its own keyframes + duration; Record flies
// them back-to-back in ONE recorder session — the camera hard-jumps between
// scenes, which lands as a clean cut in a single mp4 (no stitching needed).
// take = recorded live from the phone: dense keyframes, replayed in straight lines.
export interface VideoScene { id: string; kfs: ShotKeyframe[]; duration: number; take?: boolean }

/**
 * A recorded viewport video. blobUrl = the LOCAL recording (instant preview +
 * download, no upload needed); videoUrl/firstFrameUrl = the uploaded copies
 * (used later by the video recipe).
 */
export interface ViewportVideo { blobUrl: string; ext: string; videoUrl?: string; firstFrameUrl?: string; durationSec?: number }

/** How a studio dresses its scene for a recording, and undresses after. */
export interface TakeRecordLook {
    /** Before the first frame: hide helpers, paint the background the video gets. */
    enter: (renderer: THREE.WebGLRenderer, scene: THREE.Scene | null) => Promise<void> | void;
    /** Always runs, error or not: back to the live viewport. */
    exit: (renderer: THREE.WebGLRenderer, scene: THREE.Scene | null) => void;
}

export interface StudioTakesOptions {
    /** The studio dialog is open — the phone link closes with it. */
    open: boolean;
    productUuid?: string;
    rendererRef: React.MutableRefObject<THREE.WebGLRenderer | null>;
    sceneRef: React.MutableRefObject<THREE.Scene | null>;
    cameraRef: React.MutableRefObject<THREE.Camera | null>;
    controlsRef: React.MutableRefObject<any>;
    shot: Pick<ReturnType<typeof useShotCamera>, 'camMode' | 'setCamMode' | 'toggleCamMode' | 'shotViewRef' | 'currentShotView'>;
    focalLength: number;
    /** Set the lens as the vendor would — the surface studio routes this through
     *  its own handler so a measured field of view does not win it back. */
    setLens: (mm: number) => void;
    /** A still capture is running: the phone must not move the camera under it. */
    capturing: boolean;
    /** Upload a file; returns its URL. */
    onCapture: (file: File) => Promise<string>;
    look: TakeRecordLook;
    /** Where the phone's view is aimed, in studio units (see poseTarget). */
    subjectHeight?: number;
    /** What the phone should draw — the model's placement, or the tiles'. */
    phoneSubject: () => Pick<VcamState, 'model' | 'surface'>;
    /** Called as a fly-through starts, before the camera is driven. */
    beforeFly?: () => void;
}

const TAKE_SAMPLE_MS = 200;
const twoFrames = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

export function useStudioTakes(o: StudioTakesOptions) {
    const { open, productUuid, rendererRef, sceneRef, cameraRef, controlsRef, shot, focalLength, setLens, capturing, onCapture, look, subjectHeight } = o;
    const { camMode, setCamMode, toggleCamMode, shotViewRef, currentShotView } = shot;

    const [scenes, setScenes] = useState<VideoScene[]>([{ id: 'sc-0', kfs: [], duration: 6 }]);
    const [activeSceneId, setActiveSceneId] = useState('sc-0');
    const sceneSeq = useRef(1);
    const activeScene = scenes.find((s) => s.id === activeSceneId) || scenes[0];
    const updateScene = (id: string, mut: (s: VideoScene) => VideoScene) =>
        setScenes((l) => l.map((s) => (s.id === id ? mut(s) : s)));
    const addScene = () => {
        const id = `sc-${sceneSeq.current++}`;
        setScenes((l) => [...l, { id, kfs: [], duration: 6 }]);
        setActiveSceneId(id);
    };
    const deleteScene = (id: string) => {
        setScenes((l) => {
            const next = l.filter((s) => s.id !== id);
            if (!next.length) return l;
            if (id === activeSceneId) setActiveSceneId(next[Math.max(0, l.findIndex((s) => s.id === id) - 1)].id);
            return next;
        });
    };
    // Scenes with ≥2 keyframes are recordable; empty/1-kf scenes are skipped.
    const recordableScenes = scenes.filter((s) => s.kfs.length >= 2);
    const totalDuration = recordableScenes.reduce((a, s) => a + s.duration, 0);
    const totalKfs = scenes.reduce((a, s) => a + s.kfs.length, 0);
    const [recording, setRecording] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const flying = recording || previewing; // camera is being flown through the path
    const [recordPct, setRecordPct] = useState(0);
    const [videoResult, setVideoResult] = useState<ViewportVideo | null>(null);
    const [videoError, setVideoError] = useState<string | null>(null);
    const camPathJobRef = useRef<CameraPathJob | null>(null);
    const applyKeyframe = (kf: ShotKeyframe) => {
        const cam = cameraRef.current as any;
        const c = controlsRef.current;
        if (!cam) return;
        cam.position.set(kf.view.pos[0], kf.view.pos[1], kf.view.pos[2]);
        if (c) { c.target.set(kf.view.target[0], kf.view.target[1], kf.view.target[2]); c.update(); }
        else cam.lookAt(new THREE.Vector3(kf.view.target[0], kf.view.target[1], kf.view.target[2]));
        if (cam.isPerspectiveCamera) { cam.setFocalLength(kf.lensMm); cam.updateProjectionMatrix(); }
    };
    /** Jump to a keyframe, lens included — what clicking one in the panel does. */
    const goToKeyframe = (kf: ShotKeyframe) => { applyKeyframe(kf); setLens(kf.lensMm); };
    const addKeyframe = () => {
        const v = currentShotView();
        if (!v || !activeScene) return;
        updateScene(activeScene.id, (s) => ({ ...s, kfs: [...s.kfs, { view: v, lensMm: focalLength }] }));
    };
    /** Fly the camera through a SEQUENCE of scenes (shared by Preview and Record):
     *  leaves free view, drops selections; between scenes the camera hard-jumps
     *  to the next scene's first keyframe (= a cut). Lands on the final keyframe. */
    const flySequence = async (list: VideoScene[]) => {
        if (camMode === 'edit') toggleCamMode();
        o.beforeFly?.();

        const total = list.reduce((a, s) => a + s.duration, 0) || 1;
        let elapsed = 0;
        for (const sc of list) {
            applyKeyframe(sc.kfs[0]); // instant jump = the cut between scenes
            await twoFrames();
            const base = elapsed;
            await new Promise<void>((resolve) => {
                camPathJobRef.current = {
                    kfs: sc.kfs,
                    duration: sc.duration,
                    linear: !!sc.take,
                    onProgress: (t) => setRecordPct((base + t * sc.duration) / total),
                    onDone: resolve,
                };
            });
            elapsed += sc.duration;
        }
        const lastScene = list[list.length - 1];
        const last = lastScene.kfs[lastScene.kfs.length - 1];
        applyKeyframe(last);
        setLens(last.lensMm);
    };
    /** Dry-run fly-through of the given scenes — no recorder attached. */
    const previewScenes = async (list: VideoScene[]) => {
        if (!list.length || flying) return;
        setPreviewing(true);
        const controls = controlsRef.current;
        try {
            if (controls) controls.enabled = false;
            await flySequence(list);
        } finally {
            camPathJobRef.current = null;
            if (controls) controls.enabled = true;
            setPreviewing(false);
            setRecordPct(0);
        }
    };
    /** Preview just the ACTIVE scene (iterate on one cut). */
    const previewPath = () => {
        if (activeScene && activeScene.kfs.length >= 2) previewScenes([activeScene]);
    };
    /** Preview the WHOLE sequence — every recordable scene, cuts included. */
    const previewAll = () => previewScenes(recordableScenes);

    // ── The phone as the camera. Each pose from the phone places the shot
    // camera directly (orbit controls step aside: their angle limits would
    // clamp a handheld move). Record on the phone samples the poses into a new
    // scene — a "take" — that Preview and Record replay as it was filmed.
    const lastPoseRef = useRef<VcamPose | null>(null);
    const takeRef = useRef<{ sceneId: string; kfs: ShotKeyframe[]; timer: number } | null>(null);
    const [phoneRecording, setPhoneRecording] = useState(false);
    const focalRef = useRef(focalLength);
    focalRef.current = focalLength;
    const poseKeyframe = (p: VcamPose): ShotKeyframe => ({ view: { pos: p.pos, target: poseTarget(p, subjectHeight) }, lensMm: Math.round(p.lensMm) });
    const followPose = (p: VcamPose) => {
        lastPoseRef.current = p;
        if (flying || capturing) return;
        const cam = cameraRef.current as any;
        const c = controlsRef.current;
        if (!cam) return;
        if (camMode === 'edit') { setCamMode('shot'); return; }
        const target = poseTarget(p, subjectHeight);
        if (c) { c.enabled = false; c.target.set(target[0], target[1], target[2]); }
        cam.position.set(p.pos[0], p.pos[1], p.pos[2]);
        cam.lookAt(target[0], target[1], target[2]);
        shotViewRef.current = { pos: p.pos, target };
        if (Math.abs(p.lensMm - focalRef.current) >= 1) setLens(Math.round(p.lensMm));
    };
    const startTake = () => {
        if (takeRef.current || flying) return;
        const id = `sc-${sceneSeq.current++}`;
        // A fresh scene for every take; an untouched empty scene is reused.
        setScenes((l) => {
            const empty = l.length === 1 && !l[0].kfs.length ? [] : l;
            return [...empty, { id, kfs: [], duration: 1, take: true }];
        });
        setActiveSceneId(id);
        const take = { sceneId: id, kfs: [] as ShotKeyframe[], timer: 0 };
        const sample = () => { if (lastPoseRef.current) take.kfs.push(poseKeyframe(lastPoseRef.current)); };
        sample();
        take.timer = window.setInterval(sample, TAKE_SAMPLE_MS);
        takeRef.current = take;
        setPhoneRecording(true);
    };
    const stopTake = () => {
        const take = takeRef.current;
        if (!take) return;
        window.clearInterval(take.timer);
        takeRef.current = null;
        setPhoneRecording(false);
        const kfs = take.kfs.length >= 2 ? take.kfs : [...take.kfs, ...take.kfs].slice(0, 2);
        const duration = Math.max(0.4, Math.round(((kfs.length - 1) * TAKE_SAMPLE_MS) / 100) / 10);
        updateScene(take.sceneId, (s) => ({ ...s, kfs, duration }));
    };
    const onPhoneControl = (c: VcamControl) => {
        if (c.action === 'record') startTake();
        else if (c.action === 'stop') stopTake();
        else if (c.action === 'keyframe') {
            const p = lastPoseRef.current;
            if (!p || takeRef.current) return;
            if (activeScene && !activeScene.take) updateScene(activeScene.id, (s) => ({ ...s, kfs: [...s.kfs, poseKeyframe(p)] }));
            else {
                // A take is already a whole move: keyframes start a scene of their own.
                const id = `sc-${sceneSeq.current++}`;
                setScenes((l) => [...l, { id, kfs: [poseKeyframe(p)], duration: 6 }]);
                setActiveSceneId(id);
            }
        } else if (c.action === 'scene') addScene();
    };
    const phone = useVcamLink({ productUuid, onPose: followPose, onControl: onPhoneControl });
    // Hand the orbit controls back when the phone goes.
    useEffect(() => {
        if (phone.phoneLinked) return;
        if (takeRef.current) stopTake();
        const c = controlsRef.current;
        if (c && !c.enabled && !flying) { c.enabled = true; c.update(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone.phoneLinked]);
    // Close the link with the studio.
    const { session: phoneSession, close: closePhone, sendState: sendPhoneState } = phone;
    useEffect(() => { if (!open && phoneSession) closePhone(); }, [open, phoneSession, closePhone]);
    // Tell the phone what the studio is doing, and where the subject stands —
    // on every change, and every second for moves made with the gizmo.
    const phoneStateRef = useRef<() => void>(() => undefined);
    phoneStateRef.current = () => {
        sendPhoneState({
            recording: phoneRecording,
            scenes: scenes.length,
            scene: Math.max(1, scenes.findIndex((s) => s.id === activeScene?.id) + 1),
            keyframes: activeScene?.kfs.length ?? 0,
            ...o.phoneSubject(),
        });
    };
    useEffect(() => {
        if (phoneSession) phoneStateRef.current();
    }, [phoneSession, phone.phoneLinked, phoneRecording, scenes, activeScene]);
    useEffect(() => {
        if (!phoneSession) return undefined;
        const timer = window.setInterval(() => phoneStateRef.current(), 1000);
        return () => window.clearInterval(timer);
    }, [phoneSession]);

    const recordVideo = async () => {
        const renderer = rendererRef.current;
        if (!renderer || !recordableScenes.length || flying) return;
        setVideoError(null);
        setRecording(true);
        setRecordPct(0);
        const controls = controlsRef.current;
        const scene = sceneRef.current;
        let recStream: MediaStream | null = null;
        try {
            if (camMode === 'edit') toggleCamMode();

            if (controls) controls.enabled = false; // stops drei's update loop → the animator owns the camera
            await look.enter(renderer, scene);
            // First frame still (clean composite — usable as a reference image).
            applyKeyframe(recordableScenes[0].kfs[0]);
            await twoFrames();
            const firstBlob: Blob | null = await new Promise((resolve) => renderer.domElement.toBlob((b) => resolve(b), 'image/png', 0.92));
            // Record the canvas while the animator flies the camera. PREFER mp4:
            // the AI video models (Seedance reference-to-video, LTX render-to-real)
            // require mp4/mov, and — critically — MediaRecorder WEBM omits the
            // container duration, so fal probes it as 0s and rejects it. mp4
            // finalises with a proper duration. Fall back to webm only if the
            // browser can't record mp4 (then the worker transcodes downstream).
            const stream = (renderer.domElement as HTMLCanvasElement).captureStream(30);
            recStream = stream;
            const preferred = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
            const mime = preferred.find((m) => MediaRecorder.isTypeSupported(m)) || 'video/webm';
            const isMp4 = mime.startsWith('video/mp4');
            const outType = isMp4 ? 'video/mp4' : 'video/webm';
            const ext = isMp4 ? 'mp4' : 'webm';
            const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 12_000_000 });
            const chunks: Blob[] = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
            const stopped = new Promise<void>((resolve) => { rec.onstop = () => resolve(); });
            rec.start(250);
            await flySequence(recordableScenes);
            rec.stop();
            await stopped;
            stream.getTracks().forEach((tr) => tr.stop());
            const videoBlob = new Blob(chunks, { type: outType });
            if (!videoBlob.size) throw new Error('Recording produced no data');
            // LOCAL preview + download are available IMMEDIATELY — uploads are a
            // best-effort bonus (the URLs feed the video recipe later).
            const blobUrl = URL.createObjectURL(videoBlob);
            setVideoResult((prev) => {
                if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl);
                return { blobUrl, ext, durationSec: recordableScenes.reduce((a, s) => a + s.duration, 0) };
            });
            try {
                const videoUrl = await onCapture(new File([videoBlob], `studio3d-viewport-${Date.now()}.${ext}`, { type: outType }));
                let firstFrameUrl: string | undefined;
                if (firstBlob) {
                    firstFrameUrl = await onCapture(new File([firstBlob], `studio3d-frame0-${Date.now()}.png`, { type: 'image/png' }));
                }
                setVideoResult((prev) => (prev ? { ...prev, videoUrl, firstFrameUrl } : prev));
            } catch (upErr: any) {
                setVideoError(`Upload failed (local preview/download still available): ${upErr?.message || upErr}`);
            }
        } catch (err: any) {
            camPathJobRef.current = null;
            setVideoError(err?.message || 'Failed to record video');
        } finally {
            if (recStream) recStream.getTracks().forEach((tr) => tr.stop()); // release the canvas stream even on error
            look.exit(renderer, scene);
            if (controls) controls.enabled = true;
            setRecording(false);
            setRecordPct(0);
        }
    };

    return {
        scenes, activeScene, setActiveSceneId, updateScene, addScene, deleteScene,
        recordableScenes, totalDuration, totalKfs,
        recording, previewing, flying, recordPct, videoResult, videoError,
        camPathJobRef, applyKeyframe, goToKeyframe, addKeyframe, previewPath, previewAll, recordVideo,
        phone, phoneRecording,
    };
}

export type StudioTakes = ReturnType<typeof useStudioTakes>;
